import React, { useEffect, useMemo, useRef } from 'react';
import { Flex, Skeleton, Typography, theme } from 'antd';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { BlurringOverlay } from '../../../components/BlurringOverlay/BlurringOverlay';
import HowWeDiagnoseCard from './components/HowWeDiagnoseCard';
import MissingCrawlersCard from './components/MissingCrawlersCard';
import DiagnosticsProgressModal from './components/DiagnosticsProgressModal';
import DiagnosticsReportsTable from './components/DiagnosticsReportsTable';
import { createDiagnosticsRequestId } from './DiagnosticsAnalyticsEvents';
import { diagnosticsReportApi } from './api/diagnosticsReportApi';
import { deriveMissingCrawlers } from './MissingCrawlersDerivation';
import { useDiagnostics } from './hooks/useDiagnostics';
import type { DiagnosticsRerunLocationState } from './DiagnosticsTypes';
import { useHasTrackingCode } from '../../../hooks/usePrerenderUser';
import { useIsAccountExpired } from '../../../hooks/useIsAccountExpired';
import { useGetDiscoverabilityStatusQuery } from '../discoverability/api/discoverabilityStatusApi';
import { mapBotNamesToProviders, mapProvidersToDiagnosticsCrawlers, useAiCrawlerLineup } from '../shared';
import useTrackDiagnosticsEvent from './hooks/useTrackDiagnosticsEvent';
import type { DiscoverabilityStatusResponse } from '../discoverability/api/discoverabilityStatusApi';

const NO_INTEGRATION_OVERLAY_CONTENT =
  'Complete your integration to run diagnostics on your site.';

// Before the first successful fetch the query has no data; derive from an empty
// status so the card stays hidden until the daily evaluation resolves.
const EMPTY_STATUS: DiscoverabilityStatusResponse = {
  status: 'no_data',
  triggers: [],
  evaluatedAt: '',
  domains: [],
};

export default function DiagnosticsView() {
  const { token } = theme.useToken();
  const { diagnosticsState, runDiagnostics, dismissDiagnostics } = useDiagnostics();
  const discoverabilityStatusResult = useGetDiscoverabilityStatusQuery();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const wasLoadingRef = useRef(false);
  const tabViewedFiredRef = useRef(false);
  const trackDiagnosticsEvent = useTrackDiagnosticsEvent();
  const isNoIntegration = !useHasTrackingCode();
  const isAccountExpired = useIsAccountExpired();
  const lineup = useAiCrawlerLineup();

  const missingCrawlers = useMemo(
    () => deriveMissingCrawlers(lineup, discoverabilityStatusResult.data ?? EMPTY_STATUS),
    [lineup, discoverabilityStatusResult.data]
  );
  const missingCrawlerDomains = missingCrawlers.domains;
  const totalIntegratedDomains = missingCrawlers.integratedDomains.length;

  // Hide the entry point card for expired free trials and cancelled subscriptions.
  // `isAccountExpired` is derived synchronously from user state, so this also
  // suppresses the loading skeleton instead of flashing it before the data settles.
  // Otherwise show the card while the request is in flight so the skeleton can
  // render, and hide it once we know there are no integrated domains to report on.
  const shouldRenderEntryPointCard =
    !isAccountExpired &&
    (discoverabilityStatusResult.isLoading || totalIntegratedDomains > 0);

  // Auto-start the run requested by the report page's "Rerun diagnostics"
  // button (PRE-3136). The location state is cleared before triggering so a
  // refresh or back/forward navigation does not start another run; the ref
  // keyed on requestId covers the gap before that replace-navigation lands
  // (StrictMode double effects). Expired accounts are skipped — the run
  // endpoint rejects them anyway.
  const rerunRequest = (location.state as DiagnosticsRerunLocationState | null)?.rerunDiagnostics;
  const handledRerunRequestIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!rerunRequest || handledRerunRequestIdRef.current === rerunRequest.requestId) return;
    handledRerunRequestIdRef.current = rerunRequest.requestId;
    navigate(location.pathname, { replace: true, state: null });
    if (isAccountExpired) return;
    void runDiagnostics(rerunRequest.domain, {
      runTrigger: 'report_page_rerun_diagnostics',
      requestId: rerunRequest.requestId,
    });
  }, [rerunRequest, isAccountExpired, navigate, location.pathname, runDiagnostics]);

  useEffect(() => {
    if (diagnosticsState.loading) {
      wasLoadingRef.current = true;
    } else if (wasLoadingRef.current) {
      wasLoadingRef.current = false;
      dispatch(diagnosticsReportApi.util.invalidateTags(['DiagnosticsReports']));
    }
  }, [diagnosticsState.loading, dispatch]);

  // Gate the impression on the diagnostics content actually being visible: not
  // behind the no-integration blur overlay and after the entry-point data
  // resolves (so it does not fire under the loading skeleton).
  useEffect(() => {
    if (isNoIntegration || discoverabilityStatusResult.isLoading || tabViewedFiredRef.current) return;
    tabViewedFiredRef.current = true;
    trackDiagnosticsEvent('Diagnostics: Tab Viewed');
  }, [isNoIntegration, discoverabilityStatusResult.isLoading, trackDiagnosticsEvent]);

  const renderEntryPointCard = () => {
    if (discoverabilityStatusResult.isLoading) {
      return (
        <div data-testid="missing-crawlers-skeleton" style={{ padding: token.paddingMD }}>
          <Skeleton active />
        </div>
      );
    }

    if (discoverabilityStatusResult.isError) {
      return null;
    }

    return (
      <MissingCrawlersCard
        totalAffectedDomains={missingCrawlers.totalAffectedDomains}
        integratedDomains={missingCrawlers.integratedDomains}
        totalMissingCrawlers={missingCrawlers.totalUndetectedCrawlers}
        domains={missingCrawlerDomains}
        days={7}
        onRunDiagnostics={(domain) => {
          const requestId = createDiagnosticsRequestId();
          const targetDomain = missingCrawlerDomains.find((item) => item.domain === domain);
          trackDiagnosticsEvent('Diagnostics: Run Clicked', {
            domain,
            bots_to_test: targetDomain?.missingCrawlers.join(', ') ?? '',
            run_trigger: 'diagnostics_banner',
            request_id: requestId,
          });
          void runDiagnostics(domain, {
            runTrigger: 'diagnostics_banner',
            requestId,
            crawlers: mapProvidersToDiagnosticsCrawlers(lineup, targetDomain?.missingCrawlers ?? []),
          });
        }}
      />
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <BlurringOverlay
        enabled={isNoIntegration}
        location="AI Insights Diagnostics"
        content={NO_INTEGRATION_OVERLAY_CONTENT}
      />
      <Flex gap={token.marginLG} vertical>
        {shouldRenderEntryPointCard && renderEntryPointCard()}

        <HowWeDiagnoseCard />

        <div>
          <Typography.Title level={4} style={{ margin: 0, fontSize: token.fontSizeHeading4 }}>
            Diagnostic Reports
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginBottom: token.marginXS, fontSize: token.fontSize }}
          >
            View your diagnostic reports from last 60 days
          </Typography.Text>
          <DiagnosticsReportsTable
            onRerun={(domain, requestId) => {
              void runDiagnostics(domain, {
                runTrigger: 'diagnostics_report_table',
                requestId,
              });
            }}
          />
        </div>

        <DiagnosticsProgressModal
          open={diagnosticsState.hasDiagnosticsRun}
          domain={diagnosticsState.domain}
          loading={diagnosticsState.loading}
          checks={diagnosticsState.diagnosticsChecks}
          error={diagnosticsState.diagnosticsError}
          reportId={diagnosticsState.reportId}
          onRerun={() => {
            const requestId = createDiagnosticsRequestId();
            trackDiagnosticsEvent('Diagnostics: Run Clicked', {
              domain: diagnosticsState.domain,
              // The rerun keeps the failed run's crawler scope (see useDiagnostics).
              bots_to_test: mapBotNamesToProviders(lineup, diagnosticsState.crawlers ?? []).join(', '),
              run_trigger: 'progress_modal_error_state',
              request_id: requestId,
            });
            void runDiagnostics(diagnosticsState.domain, {
              runTrigger: 'progress_modal_error_state',
              requestId,
            });
          }}
          onClose={dismissDiagnostics}
        />
      </Flex>
    </div>
  );
}
